import { ChangeDetectorRef, Component, computed, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { Command } from "@tylertech/commands-module";
import { FileService, RouteInfoService, ThemeService } from "@tylertech/core-module";
import { LgdFormControl, LgdFormControlFactory, LgdFormControlSelectConfig } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { StringHelper, Utilities } from "@tylertech/shared-module";
import { CommonChartData, IChartCriteria, ProfileSettingViewModel, } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ChartDialogService, ChartRegistryService } from "../../services";
import { LgdChartComponent } from "../lgdChart.component";
import { OnDatesChangeArgs } from "./commonChartDateOptions.component";

interface ExtendedCommonChartData
    extends CommonChartData
{
    $controlName?: string;
}

@Component({
    selector: "common-chart",
    templateUrl: "./commonChart.tmpl.html",
    styleUrls: ["./commonChart.component.scss"],
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommonChartComponent implements OnInit, OnDestroy
{
    private readonly log = getLoggerFor("CommonChartComponent");
    private readonly destructor$ = new Subject<void>();

    // TODO - mutually exclusive inputs are a sign that this component should be split apart
    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Input("chartCriteria") readonly input_chartCriteria: IChartCriteria;

    // TODO - mutually exclusive inputs are a sign that this component should be split apart
    @Input("data") readonly singleChartData: CommonChartData;

    readonly isLoaded = signal(false);
    readonly hasChartCriteria = signal(false);

    readonly commonChartDatas = signal<Array<ExtendedCommonChartData>>([]);
    // /**
    //  * Set to `true` if the @Input("data") was used, or if the returned
    //  * chartCriteria has at least one CommonChartData object defined.
    //  */
    // readonly hasData = signal(false);
    readonly hasData = computed(() =>
    {
        const dataCount = this.commonChartDatas().length;
        this.log.debug(`computing hasData`, { dataCount });
        return dataCount > 0;
    });

    readonly showShareAction = computed(() =>
    {
        const hasData = this.hasData();
        const firstChartDataShowShare = this.commonChartDatas()[0]?.ShowShare === true;
        this.log.debug(`computing showShareAction`, { hasData, firstChartDataShowShare });
        return this.hasData()
            && firstChartDataShowShare;
    });

    readonly firstCommonChartData = computed(() =>
    {
        const chartData = this.commonChartDatas();
        this.log.debug(`computing firstCommonChartData`, { chartData });
        return chartData.length ? chartData[0] : null;
    });

    showChartSettings: boolean;
    private _chartCriteria: IChartCriteria;
    chartTitle: string;
    chartSubtitle: string;
    mixedChart: boolean;
    readonly actionsCommands: ReadonlyArray<Command>;
    readonly chartsFormGroup: FormGroup;

    constructor(
        private readonly $themeService: ThemeService,
        private readonly $fileService: FileService,
        private readonly $routeInfoService: RouteInfoService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly changeDetectionRef: ChangeDetectorRef
    )
    {
        this.chartsFormGroup = new FormGroup({});
    }

    get chartCriteria(): IChartCriteria { return this._chartCriteria || this.input_chartCriteria; }
    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartProviderId(): string { return this.chartCriteria?.ProviderId ?? ""; }

    // get commonChartDatas(): ExtendedCommonChartData[]
    // {
    //     return this._commonChartDatas || this.chartCriteria.CommonChartDatas;
    // }

    // TODO - convert to computed signal
    get showChartLabels(): boolean
    {
        return !!this.firstCommonChartData() && !!this.firstCommonChartData().InfoCards;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (!this.singleChartData && !this.chartCriteria)
        {
            this.log.warning(`No data was passed into component.`);
            return;
        }

        let initializingPromise: Promise<void>;
        if (this.singleChartData)
        {
            initializingPromise = Promise.resolve(this.singleChartData)
                .then((data) =>
                {
                    this.chartTitle = data.Title;
                    this.chartSubtitle = data.Subtitle;

                    this.commonChartDatas.set([data]);
                });
        }
        else if (this.chartCriteria.CommonChartDatas)
        {
            initializingPromise = Promise.resolve(this.chartCriteria.CommonChartDatas)
                .then((data) =>
                {
                    this.processChartData(data);
                });
        }
        else
        {
            this.hasChartCriteria.set(true);
            this.chartTitle = this.chartCriteria.Title;
            initializingPromise = this.loadChartData();
        }

        initializingPromise
            .finally(() =>
            {
                this.isLoaded.set(true);
                this.changeDetectionRef.markForCheck();
            });
    }

    displayChartParameterDialogAndReload(): void
    {
        const s = Utilities.stringify(this.chartCriteria.Parameters);
        this.$chartDialogService.show(this.chartCriteria.Parameters, this.chartTitle)
            .then(
                (result: ProfileSettingViewModel[]) =>
                {
                    if (s !== Utilities.stringify(result))
                    {
                        this.chartCriteria.Parameters = result;
                        return this.loadChartData();
                    }
                },
                err =>
                {
                    this.log.warning(`displayChartParameterDialogAndReload: error showing chart dialog`, err);
                });
    }

    protected loadChartData(): Promise<void>
    {
        this.commonChartDatas.set([]);
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    if (!result.CommonChartDatas?.length)
                    {
                        return;
                    }

                    this.processChartData(result.CommonChartDatas);

                    this.chartCriteria = result;
                },
                error =>
                {
                    console.error("Could not retrieve chart data", error);
                }
            );
    }

    private processChartData(data: Array<ExtendedCommonChartData>): void
    {
        for (const x of data)
        {
            this.setupFormControl(x);

            /*
             * ! Note: these assignments effectively make only the settings on the last CommonChartData meaningful!
             */

            this.showChartSettings = !this.singleChartData && x.UseChartSettings;
            this.chartTitle = x.Title;
            this.chartSubtitle = x.Subtitle;
        }

        this.commonChartDatas.set(data);
    }

    private setupFormControl(data: ExtendedCommonChartData): void
    {
        const controlName = data.$controlName ?? (data.$controlName = StringHelper.toCamelCase(data.Title));

        let control = this.chartsFormGroup.get(controlName) as LgdFormControl<LgdFormControlSelectConfig<string>>;
        if (!control)
        {
            this.log.debug(`setupFormControl | creating control for "${controlName}"`, { chart: data });
            control = LgdFormControlFactory.select();
            this.chartsFormGroup.addControl(controlName, control);
            this.addValueChangedListener(control, data);
        }
        control.setValue(data.SelectedDrillDownOption, { emitEvent: false });
    }

    // @LogInvocation(LogLevel.DEBUG)
    private addValueChangedListener(control: AbstractControl, chart: CommonChartData): void
    {
        control.valueChanges
            .pipe(takeUntil(this.destructor$))
            .subscribe((value: string) =>
            {
                chart.SelectedDrillDownOption = value;
                this.selectedDrillDownChange();
            });
    }

    private selectedDrillDownChange(): void
    {
        this.resetChartPaginator();
        this.loadChartData();
    }

    getOptions(chart: CommonChartData): Observable<ReadonlyArray<string>>
    {
        return new BehaviorSubject(chart.DrillDownOptions).asObservable();
    }

    onDateOptionChange(dateOption: string): void
    {
        // TODO - why are we messing with the firstCommonChartData? The template implies that each CommonChartData can have a separate set of DateOption controls
        if (this.firstCommonChartData().SelectedDateOption !== dateOption)
        {
            this.firstCommonChartData().SelectedDateOption = dateOption;
            this.loadChartData();
        }
    }

    onDateChange($event: OnDatesChangeArgs): void
    {
        // TODO - this can never be false.. the paginator can only be present in the template if we have at least one CommonChartData object....
        if (!this.hasData())
        {
            return;
        }
        const startDate = $event.startDate;
        const endDate = $event.endDate;
        // TODO - why are we messing with the firstCommonChartData? The template implies that each CommonChartData can have a separate set of DateOption controls
        this.firstCommonChartData().StartDate = startDate;
        this.firstCommonChartData().EndDate = endDate;
        this.firstCommonChartData().SelectedDateOption = "CUSTOM";
        this.loadChartData();
    }

    getViewMoreUrl(viewMoreUrl: string): string
    {
        const tenant = this.$routeInfoService.tenant;
        return `/${tenant}${viewMoreUrl}`;
    }

    pageChange(event: PageEvent): void
    {
        if (this.firstCommonChartData().ChartPaginator.PageIndex !== event.pageIndex)
        {
            this.firstCommonChartData().ChartPaginator.PageIndex = event.pageIndex;
            this.loadChartData();
        }
    }

    protected resetChartPaginator(): void
    {
        // TODO - why are we messing with the firstCommonChartData? The template implies that each CommonChartData can have a separate set of DateOption controls
        if (!Utilities.IsNullOrUndefined(this.firstCommonChartData().ChartPaginator))
        {
            this.firstCommonChartData().ChartPaginator.PageIndex = 0;
        }
    }

    shareChart(lgdChart: LgdChartComponent): void
    {
        const theme = this.$themeService.currentTheme;
        const backgroundColor = theme === this.$themeService.darkTheme ? "#424242" : "white";

        const srcCanvas = lgdChart.getCanvas();

        const canvas = document.createElement("canvas");
        canvas.width = srcCanvas.width;
        canvas.height = srcCanvas.height;

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(srcCanvas, 0, 0);

        const img = canvas.toDataURL("image/png").replace("data:image/png;base64,", "");
        this.$fileService.openDocumentLink(img, "image/png", lgdChart.data.Title.replace(/ /g, ""));
    }
}
